import Icon from 'components/Icon';
import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { CampaignQuestion } from 'types';
import { EXCLUDE_SPECIAL_QUESTIONS } from 'views/Administration/CustomSurveys/consts';
import CardHeaderTitle from 'views/Widgets/Cards/CardHeaderTitle';
import QuestionsEditor from 'views/Widgets/Inputs/QuestionsEditor';

export type ArchivableQuestion = Record<string, unknown> & {
  archived?: boolean;
};

interface Props {
  value?: ArchivableQuestion[];
  onChange: (value: ArchivableQuestion[]) => void;
  // what archiving means for this list, shown in the confirmation dialog
  archiveDescription: string;
}

// A list of promotion packet questions where removing one archives it
// instead of deleting it: answers are stored keyed by question name, so
// the definition must stay around for the label to keep showing next to
// them. Archive and restore are edits of the form value; nothing is saved
// until the settings form is submitted.
const PromotionPacketQuestionsInput: FC<Props> = ({
  value,
  onChange,
  archiveDescription,
}) => {
  const { formatMessage } = useIntl();

  const questions = useMemo(() => value ?? [], [value]);
  const activeQuestions = useMemo(
    () => questions.filter((q) => !q.archived),
    [questions]
  );
  const archivedQuestions = useMemo(
    () => questions.filter((q) => q.archived),
    [questions]
  );

  const handleActiveChange = useCallback(
    (changed: ArchivableQuestion[]) => {
      // the question editor rewrites the name on every edit; the name is the
      // answer key, so an edited question keeps the one it was saved with
      const newActive =
        changed.length === activeQuestions.length
          ? changed.map((q, index) => {
              const previous = activeQuestions[index];
              const renamed =
                q.name !== previous.name &&
                !changed.some((other) => other.name === previous.name);
              return renamed ? { ...q, name: previous.name } : q;
            })
          : changed;
      const removed = activeQuestions.filter(
        (q) => !newActive.some((n) => n.name === q.name)
      );
      onChange([
        ...newActive,
        ...archivedQuestions,
        ...removed.map((q) => ({ ...q, archived: true })),
      ]);
    },
    [activeQuestions, archivedQuestions, onChange]
  );

  const restore = useCallback(
    (question: ArchivableQuestion) => {
      const { archived, ...rest } = question;
      onChange([
        ...activeQuestions,
        rest,
        ...archivedQuestions.filter((q) => q !== question),
      ]);
    },
    [activeQuestions, archivedQuestions, onChange]
  );

  const restoreLabel = formatMessage({
    id: 'app.views.administration.content_administration.templates.actions.restore',
    defaultMessage: 'Restore',
  });

  const archiveLabels = useMemo(
    () => ({
      action: formatMessage({
        id: 'app.views.administration.content_administration.templates.actions.archive',
        defaultMessage: 'Archive',
      }),
      title: formatMessage({
        id: 'app.views.administration.content_administration.templates.promo.settings.packet_questions.archive_modal.title',
        defaultMessage: 'Archive question',
      }),
      description: archiveDescription,
    }),
    [archiveDescription, formatMessage]
  );

  return (
    <>
      <QuestionsEditor
        value={activeQuestions as unknown as CampaignQuestion[]}
        onChange={handleActiveChange as (newList: object[]) => void}
        excludeSpecialQuestions={EXCLUDE_SPECIAL_QUESTIONS}
        submitButtonType="button"
        // the settings form owns the route-leave prompt; a second one from
        // the modal would cancel it when the modal closes
        disableUnsavedChangesPrompt={true}
        deleteLabels={archiveLabels}
      />
      {archivedQuestions.length > 0 && (
        <Card className="mt-4 mb-0">
          <CardHeader>
            <CardHeaderTitle>
              <FormattedMessage
                id="app.views.administration.content_administration.templates.promo.settings.packet_questions.archived_title"
                defaultMessage="Archived questions"
              />
            </CardHeaderTitle>
          </CardHeader>
          <CardBody>
            <ListGroup className="list-group-flush my-n3">
              {archivedQuestions.map((question, index) => (
                <ListGroupItem
                  key={String(question.name ?? index)}
                  className="d-flex align-items-center justify-content-between"
                >
                  <span>{String(question.label ?? question.name ?? '')}</span>
                  <Button
                    color="white"
                    onClick={() => restore(question)}
                    title={restoreLabel}
                    aria-label={restoreLabel}
                  >
                    <Icon icon={ICONS.REFRESH} />
                  </Button>
                </ListGroupItem>
              ))}
            </ListGroup>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default React.memo(PromotionPacketQuestionsInput);
